'use client';

import { useState } from 'react';
import { tv } from '../_lib/utils';
import type { SanityImage } from '../types';
import { Container } from '../Container';
import { Media } from '../Media';
import { PanelStory, type PanelStoryProps } from '../Panel';
import { Popup } from '../Popup';
import { useBrandValue, type Brands } from '../Theme';

export const itemStyles = tv({
	slots: {
		base: 'aspect-4/3 rounded-md overflow-hidden [&>div]:h-full [&_img]:h-full max-md:col-span-2 md:row-span-2',
	},
	variants: {
		index: {
			0: {
				base: 'max-md:col-start-2',
			},
			1: {
				base: 'md:col-start-1',
			},
			2: {
				base: 'md:col-start-1',
			},
			3: {
				base: 'max-md:col-start-3 md:col-start-2 md:row-start-2',
			},
			4: {
				base: 'md:col-start-2 md:row-start-4',
			},
			5: {
				base: 'max-md:col-start-2 md:col-start-3 md:row-start-3',
			},
			6: {
				base: 'md:col-start-3 md:row-start-5',
			},
			7: {
				base: 'max-md:col-start-1 md:col-start-4 md:row-start-2',
			},
			8: {
				base: 'md:col-start-4 md:row-start-4',
			},
			9: {
				base: 'md:col-start-4 md:row-start-6',
			},
			10: {
				base: 'max-md:col-start-2 md:col-start-5 md:row-start-1',
			},
			11: {
				base: 'md:col-start-5 md:row-start-3',
			},
			12: {
				base: 'md:col-start-5 md:row-start-5',
			},
		},
		hasPopup: {
			true: {
				base: '[&_img]:transition-transform [&_img]:duration-normal [&_img]:ease-smooth hover:[&_img]:scale-105',
			},
		},
	},
});

export type ImageTileProps = {
	defaultBrand?: Brands;
	image: SanityImage;
	index: number;
	panel?: PanelStoryProps;
};

export type ImageTilesProps = {
	className?: string;
	defaultBrand?: Brands;
	items: Omit<ImageTileProps, 'index'>[];
};

export function ImageTiles(props: ImageTilesProps) {
	return (
		<div className={`${props.className || ''}`} data-component='ImageTiles'>
			<Container className={`grid grid-cols-7 gap-4 md:grid-rows-7 md:grid-cols-5 lg:gap-6 xl:gap-8`}>
				{props.items.map((item, index) => {
					if (index > 12) {
						return;
					}

					return <CustomerCardPopup key={`image-tiles-${index}`} {...item} defaultBrand={props.defaultBrand} index={index} />;
				})}
			</Container>
		</div>
	);
}

export function CustomerCardPopup(props: ImageTileProps) {
	const { base } = itemStyles({ index: props.index, hasPopup: props.panel ? true : false });
	const [open, setOpen] = useState(false);

	if (!props.panel) {
		return <Media image={props.image} className={base()} aspectRatio='4/3' />;
	}

	return (
		<Popup
			brand={props.defaultBrand ?? useBrandValue()}
			open={open}
			setOpen={setOpen}
			layout={'sidebar'}
			triggerButton={
				<button aria-label={`Read more: ${props.panel.title}`} onClick={() => setOpen(true)} className={base()}>
					<Media
						image={props.image}
						className={base()}
						aspectRatio='4/3'
						nextImageSizes='(max-width: 768px) 30vw, (max-width: 1200px) 25vw, 20vw'
					/>
				</button>
			}
		>
			{props.panel && <PanelStory {...props.panel} />}
		</Popup>
	);
}
